import React, { useEffect, useState } from 'react';
import '../styles.css';
import 'flatpickr/dist/flatpickr.min.css';
import Sidebar from './Sidebar';
import StatCard from './StatCard';
import ActivityTable from './ActivityTable';
import { useNavigate } from "react-router-dom";
import { Session } from "../components/utils/session";
import { API_BASE_URL } from '../config';

export default function DashboardContent() {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [mode, setMode] = useState('daily');
  const [summary, setSummary] = useState({productivityPercent: 0, neutralPercent: 0,});
  const [summaryError, setSummaryError] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const navigate = useNavigate();
  const role = Session.getRole();
  const token = Session.getToken();

  useEffect(() => {
    if (role === "Member" || role === "Team Leader") {
      navigate("/dailytask", { replace: true });
    }
  }, [role, navigate]);

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar(s => !s);
    } else {
      setHideDesktop(s => !s);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!token) return;

    fetch(`${API_BASE_URL}/api/Workspace/Workspaces_List`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        try {
          return await res.json();
        } catch {
          return [];
        }
      })
      .then(data => setWorkspaces(data || []))
      .catch(err => console.error('Workspace load error', err));
  }, [token]);

  useEffect(() => {
    const controller = new AbortController();
    const loadSummary = async () => {
      try {
        setSummaryError(null);
        const endpoint = mode === 'daily' ? 'today' : 'weekly';
        const res = await fetch(`${API_BASE_URL}/api/Productivity/${endpoint}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        setSummary({
          productivityPercent: data.productivityPercent ?? 0,
          neutralPercent: data.neutralPercent ?? 0,
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        setSummaryError(err.message || 'Failed to load summary');
        setSummary({ productivityPercent: 0, neutralPercent: 0 });
      }
    };
    loadSummary();
    return () => controller.abort();
  }, [mode]);
  
useEffect(() => {
  if (mode === 'weekly') {
    setSelectedDate('');
  }
}, [mode]);

  const prod = Math.round(summary.productivityPercent || 0);
  const neutral = Math.round(summary.neutralPercent || 0);

  const statCards = [
    {
      icon: 'fas fa-chart-line',
      value: `${prod}%`,
      label: 'Members Productivity',
      color: 'icon-green',
    },
    {
      icon: 'fas fa-grip-lines-vertical',
      value: `${neutral}%`,
      label: 'Members Neutral',
      color: 'icon-blue',
    },
  ];

  return (
    <div>
      {/* Overlay for mobile sidebar - always rendered, visibility controlled by useEffect */}
      <div id="sidebar-overlay" className="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />
      <div id="sidebar">
        <Sidebar />
      </div>
      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button
            type="button"
            className="btn btn-toggle me-3"
            onClick={toggleSidebar}
          >
            <i className="fas fa-bars" />
          </button>
          <a href="/dashboard" className="text-muted text-decoration-none small">
            <i className="fas fa-chevron-left" /> Back
          </a>
        </div>
        <h2 className="mb-4">Productivity Reports</h2>
        <div className="row g-3 mb-4 filter-row align-items-end">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="filter-box">
              <input
                type="radio"
                className="btn-check"
                id="daily"
                checked={mode === 'daily'}
                onChange={() => setMode('daily')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="daily">
                Daily
              </label>
              <input
                type="radio"
                className="btn-check"
                id="weekly"
                checked={mode === 'weekly'}
                onChange={() => setMode('weekly')}
              />
              <label className="btn btn-outline-custom px-3" htmlFor="weekly">
                Weekly
              </label>
            </div>
          </div>
          <div className="col-6 col-md-6 col-xl-3">
            <select
              className="form-select reportsInput"
              value={selectedWorkspace}
              onChange={(e) => setSelectedWorkspace(e.target.value)}
            >
              <option value="">All Teams</option>
              {workspaces.map(w => (
                <option key={w.workspaceID} value={w.workspaceID}>
                  {w.workspaceName}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-6 col-xl-3">
            <input
              type="text"
              className="form-control reportsInput"
              placeholder="Search employee"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
            />
          </div>
         {mode === 'daily' && (
          <div className="col-6 col-md-6 col-xl-3">
            <input
              type="date"
              className="form-control reportsInput"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          )}
        </div>
        <div className="row g-3 mb-4">
          {statCards.map(card => (
            <div className="col-12 col-lg-6 col-xl-6" key={card.label}>
              <StatCard {...card} />
            </div>
          ))}
        </div>
        <div className="main-card">
          <h5 className="fw-bold mb-3">
            {mode === 'daily' ? 'Daily Activity' : 'Weekly Activity'}
          </h5>
          <ActivityTable
            mode={mode}
            WorkspaceID={selectedWorkspace}
            employeeName={employeeName}
            selectedDate={selectedDate}
          />
        </div>
      </div>
    </div>
  );
}
